import { WRITABLE_VISUAL_MODEL_TYPE, } from "./writable-visual-model.js";
import { VISUAL_MODEL_TYPE, VisualModelDataVersion, } from "./visual-model.js";
import { isModelVisualInformation, isVisualDiagramNode, isVisualNode, isVisualRelationship, isVisualView, VISUAL_DIAGRAM_NODE_TYPE, VISUAL_GROUP_TYPE, VISUAL_MODEL_DATA_TYPE, VISUAL_NODE_TYPE, VISUAL_PROFILE_RELATIONSHIP_TYPE, VISUAL_RELATIONSHIP_TYPE, VISUAL_VIEW_TYPE } from "./concepts/index.js";
import { addToMapArray, removeFromMapArray } from "@dataspecer/utilities";
import { isVisualModelSerializationV0, isVisualModelSerializationV1, isVisualModelSerializationV2, visualModelSerializationV0ToV2, visualModelSerializationV1ToV2, } from "./serialization/index.js";
export class DefaultVisualModel {
    identifier;
    observers = [];
    /**
     * Map of all visual entities in the {@link model}.
     */
    entities = new Map();
    /**
     * Cached mapping from represented to entity identifiers.
     */
    representedToEntity = new Map();
    /**
     * Map from model identifier to the model data.
     */
    models = new Map();
    /**
     * When model is loaded version of the source data is saved here.
     * This allows for additional migration on higher level.
     */
    sourceDataVersion = VisualModelDataVersion.VERSION_1;
    /**
     * Cached visual view identifier or null when there is no such entity.
     */
    visualViewIdentifier = null;
    constructor(identifier) {
        this.identifier = identifier;
    }
    getTypes() {
        return [VISUAL_MODEL_TYPE, WRITABLE_VISUAL_MODEL_TYPE];
    }
    getId() {
        return this.identifier;
    }
    getIdentifier() {
        return this.identifier;
    }
    getVisualEntity(identifier) {
        return this.entities.get(identifier) ?? null;
    }
    getVisualEntitiesForRepresented(represented) {
        const identifiers = this.representedToEntity.get(represented);
        if (identifiers === undefined) {
            return [];
        }
        const visualEntities = identifiers
            .map(identifier => this.getVisualEntity(identifier))
            .filter(visualEntity => visualEntity !== null);
        return visualEntities;
    }
    hasVisualEntityForRepresented(represented) {
        const identifiers = this.representedToEntity.get(represented);
        return identifiers !== undefined && identifiers.length > 0;
    }
    /**
     * Return all entities from the visual model.
     */
    getVisualEntities() {
        // Create ad return a copy of the entities.
        const result = new Map();
        for (const [identifier, entity] of this.entities) {
            result.set(identifier, entity);
        }
        return result;
    }
    subscribeToChanges(listener) {
        this.observers.push(listener);
        // Return callback to remove the listener.
        return () => {
            this.observers = this.observers.filter(item => item !== listener);
        };
    }
    getModelColor(identifier) {
        return this.models.get(identifier)?.color ?? null;
    }
    getModelsData() {
        return new Map(this.models);
    }
    getInitialModelVersion() {
        return this.sourceDataVersion;
    }
    getLabel() {
        const entity = this.getEntitySync(modelEntityIdentifier(this.identifier));
        if (entity === null) {
            return null;
        }
        const modelEntity = entity;
        return modelEntity.label;
    }
    getEntitySync(identifier) {
        return this.entities.get(identifier) ?? null;
    }
    setLabel(label) {
        const id = modelEntityIdentifier(this.identifier);
        if (this.getEntitySync(id) === null) {
            // Create model entity.
            const modelEntity = {
                type: [VISUAL_MODEL_ENTITY_TYPE],
                label,
            };
            this.createEntity(modelEntity, id);
        }
        else {
            // Update.
            const change = {
                label: label
            };
            this.updateVisualEntity(id, change);
        }
    }
    //
    // Operation implementation section
    //
    addVisualNode(entity) {
        // This will trigger update in underlying model and invoke callback.
        // We react to changes using the callback.
        return this.createEntity({
            ...entity,
            type: [VISUAL_NODE_TYPE],
        });
    }
    addVisualDiagramNode(entity) {
        // This will trigger update in underlying model and invoke callback.
        // We react to changes using the callback.
        return this.createEntity({
            ...entity,
            type: [VISUAL_DIAGRAM_NODE_TYPE],
        });
    }
    addVisualRelationship(entity) {
        // This will trigger update in underlying model and invoke callback.
        // We react to changes using the callback.
        return this.createEntity({
            ...entity,
            type: [VISUAL_RELATIONSHIP_TYPE],
        });
    }
    addVisualProfileRelationship(entity) {
        // This will trigger update in underlying model and invoke callback.
        // We react to changes using the callback.
        return this.createEntity({
            ...entity,
            type: [VISUAL_PROFILE_RELATIONSHIP_TYPE],
        });
    }
    addVisualGroup(entity) {
        // This will trigger update in underlying model and invoke callback.
        // We react to changes using the callback.
        return this.createEntity({
            ...entity,
            type: [VISUAL_GROUP_TYPE],
        });
    }
    updateVisualEntity(identifier, entity) {
        // This will trigger update in underlying model and invoke callback.
        // We react to changes using the callback.
        this.changeEntity(identifier, entity);
    }
    deleteVisualEntity(identifier) {
        // This will trigger update in underlying model and invoke callback.
        // We react to changes using the callback.
        this.deleteEntity(identifier);
    }
    setModelColor(identifier, color) {
        const entityIdentifier = this.models.get(identifier)?.id;
        if (entityIdentifier === undefined) {
            // We need to create new model entity.
            this.createModelEntity(identifier, color);
            return;
        }
        const entity = this.getEntitySync(entityIdentifier);
        if (entity === null) {
            // We need to create new model entity.
            this.createModelEntity(identifier, color);
            return;
        }
        // This will trigger update in underlying model and invoke callback.
        // We react to changes using the callback.
        this.changeEntity(entityIdentifier, { color });
    }
    createModelEntity(model, color) {
        // This will trigger update in underlying model and invoke callback.
        // We react to changes using the callback.
        // The same way to create a ModelVisualInformation is used in the
        // deserializeModelV0 method!
        const entity = {
            type: [VISUAL_MODEL_DATA_TYPE],
            representedModel: model,
            color
        };
        this.createEntity(entity);
    }
    deleteModelColor(identifier) {
        // There is only color, so we delete the entity.
        this.deleteModelData(identifier);
    }
    deleteModelData(identifier) {
        // We need to get entity identifier.
        const entityIdentifier = this.models.get(identifier)?.id;
        if (entityIdentifier === undefined) {
            return;
        }
        // Now we delete the entity.
        // This will trigger update in underlying model and invoke callback.
        // We react to changes using the callback.
        this.deleteEntity(entityIdentifier);
    }
    setView(view) {
        if (this.visualViewIdentifier === null) {
            this.visualViewIdentifier = this.createEntity({
                ...view,
                type: [VISUAL_VIEW_TYPE],
            });
        }
        else {
            this.changeEntity(this.visualViewIdentifier, {
                ...view,
                type: [VISUAL_VIEW_TYPE],
            });
        }
    }
    //
    // Entity update operations.
    //
    createEntity(entity, id = undefined) {
        if (id === undefined) {
            id = createIdentifier();
        }
        this.entitiesDidChange([{ ...entity, id }], [], []);
        return id;
    }
    changeEntity(id, entity) {
        const previous = this.entities.get(id);
        if (previous === undefined) {
            console.warn("Update called for non-existing entity.", { id, entity, available: this.entities.keys() });
            return;
        }
        this.entitiesDidChange([], [
            { ...previous, ...entity, id }
        ], []);
    }
    deleteEntity(id) {
        this.entitiesDidChange([], [], [id]);
    }
    /**
     * Called on change in entities.
     * The local entities field has been already modified.
     */
    entitiesDidChange(created, changed, removed) {
        created.forEach(entity => {
            this.entities.set(entity.id, entity);
            this.onEntityDidCreate(entity);
            this.notifyObserversOnEntityChange(null, entity);
        });
        changed.forEach(entity => {
            const previous = this.entities.get(entity.id);
            this.entities.set(entity.id, entity);
            this.onEntityDidChange(previous, entity);
            this.notifyObserversOnEntityChange(previous, entity);
        });
        removed.forEach(id => {
            const previous = this.entities.get(id);
            if (previous === undefined) {
                return;
            }
            this.entities.delete(id);
            this.onEntityDidRemoved(previous);
            this.notifyObserversOnEntityChange(previous, null);
        });
    }
    onEntityDidCreate(entity) {
        if (isVisualDiagramNode(entity)) {
            addToMapArray(entity.representedVisualModel, entity.id, this.representedToEntity);
        }
        if (isVisualNode(entity)) {
            addToMapArray(entity.representedEntity, entity.id, this.representedToEntity);
        }
        if (isVisualRelationship(entity)) {
            addToMapArray(entity.representedRelationship, entity.id, this.representedToEntity);
        }
        if (isModelVisualInformation(entity)) {
            this.models.set(entity.representedModel, entity);
            this.notifyObserversOnModelChange(null, entity);
        }
        if (isVisualView(entity)) {
            this.visualViewIdentifier = entity.id;
        }
    }
    notifyObserversOnEntityChange(previous, next) {
        this.observers.forEach(observer => observer.visualEntitiesDidChange([{ previous, next }]));
    }
    notifyObserversOnModelChange(previous, next) {
        // We know that at leas one of then is not null, unfortunately TS
        // is not capable of detecting that.
        const modelIdentifier = (previous?.representedModel ?? next?.representedModel);
        const color = next?.color ?? null;
        this.observers.forEach(observer => observer.modelColorDidChange(modelIdentifier, color));
    }
    onEntityDidChange(previous, entity) {
        if (isModelVisualInformation(previous) && isModelVisualInformation(entity)) {
            this.models.set(entity.representedModel, entity);
            this.notifyObserversOnModelChange(previous, entity);
        }
        if (isVisualView(entity)) {
            this.visualViewIdentifier = entity.id;
        }
    }
    onEntityDidRemoved(previous) {
        const id = previous.id;
        // Notify listeners.
        if (isVisualDiagramNode(previous)) {
            removeFromMapArray(this.representedToEntity, previous.representedVisualModel, id);
        }
        if (isVisualNode(previous)) {
            removeFromMapArray(this.representedToEntity, previous.representedEntity, id);
        }
        if (isVisualRelationship(previous)) {
            removeFromMapArray(this.representedToEntity, previous.representedRelationship, id);
        }
        if (isModelVisualInformation(previous)) {
            // We also need to delete from model.
            this.models.delete(previous.representedModel);
            // This meas change in color.
            this.notifyObserversOnModelChange(previous, null);
        }
        if (isVisualView(previous)) {
            this.visualViewIdentifier = null;
        }
    }
    //
    // Serialization
    //
    serializeModel() {
        const serialization = {
            identifier: this.identifier,
            version: 2,
            type: "http://dataspecer.com/resources/local/visual-model",
            entities: [...this.entities.values()],
        };
        return serialization;
    }
    deserializeModel(value) {
        let entities = [];
        if (isVisualModelSerializationV0(value)) {
            this.sourceDataVersion = VisualModelDataVersion.VERSION_0;
            const v1 = visualModelSerializationV0ToV2(value);
            const v2 = visualModelSerializationV1ToV2(v1);
            entities = v2.entities;
        }
        else if (isVisualModelSerializationV1(value)) {
            this.sourceDataVersion = VisualModelDataVersion.VERSION_1;
            const v2 = visualModelSerializationV1ToV2(value);
            entities = v2.entities;
        }
        else if (isVisualModelSerializationV2(value)) {
            entities = value.entities;
        }
        else {
            throw new Error(`Unsupported serialization of visual model.`);
        }
        // Load the entities.
        this.entities = new Map();
        // Load from entities by just adding one entity at a time.
        for (const entity of entities) {
            this.entities.set(entity.id, entity);
            this.onEntityDidCreate(entity);
        }
        return this;
    }
}
/**
 * @returns New entity identifier.
 */
const createIdentifier = () => (Math.random() + 1).toString(36).substring(7);
/**
 * @returns Identifier of the entity inside the model used to store model information.
 */
const modelEntityIdentifier = (identifier) => {
    return identifier + "-model-metadata-entity";
};
export const VISUAL_MODEL_ENTITY_TYPE = "entity-model-type";
//# sourceMappingURL=default-visual-model.js.map